import React, { useState, useEffect, useCallback, useRef } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import axios from 'axios';
import { User, Maximize, Settings, X, Radio, Square } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DisplayPage() {
  const [competitions, setCompetitions] = useState([]);

  // Page title
  usePageTitle("Display");

  const [selectedCompetitionId, setSelectedCompetitionId] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [availableRounds, setAvailableRounds] = useState([]);
  const [competition, setCompetition] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Display state
  const [timer, setTimer] = useState(null);
  const [displayTime, setDisplayTime] = useState(0);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [nextUp, setNextUp] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Rotation state for alternating between gymnast info and scores
  const [displayMode, setDisplayMode] = useState('info'); // 'info' or 'scores'
  const [latestScores, setLatestScores] = useState({});
  
  // Presentation state
  const [presentationState, setPresentationState] = useState(null);

  // Display control (sponsor modes)
  const [displayControlMode, setDisplayControlMode] = useState('normal');
  const [sponsors, setSponsors] = useState([]);
  const [, setClockTick] = useState(0);  // wall-clock sync placeholder (see below)
  const prevTimerRunningRef = useRef(false);
  
  // Stop an active presentation directly from the display screen
  const stopPresentation = async () => {
    if (!selectedCompetitionId) return;
    try {
      await axios.post(`${API}/presentation/stop`, { competitionId: selectedCompetitionId });
      setPresentationState(null);
    } catch (e) {
      console.error('Failed to stop presentation', e);
    }
  };

  // Presentation timer state (moved to parent to survive re-renders)
  const [showAllResults, setShowAllResults] = useState(false);
  const [firstRevealedAt, setFirstRevealedAt] = useState(null);
  const timerStartedRef = useRef(false);
  
  // Screen scaling for 16:9 TV displays
  const [scale, setScale] = useState(1);
  
  // Calculate scale to fit 16:9 aspect ratio
  // Use different scaling for presentations vs normal display
  useEffect(() => {
    const calculateScale = () => {
      const targetWidth = 1920;
      const targetHeight = 1080;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      const scaleX = windowWidth / targetWidth;
      const scaleY = windowHeight / targetHeight;
      
      // For presentations: use MAX to FILL the screen (no black bars)
      // For normal display: use MIN to FIT within screen (may have black bars but no overflow)
      const newScale = presentationState?.isActive 
        ? Math.max(scaleX, scaleY)  // Fill for presentations
        : Math.min(scaleX, scaleY); // Fit for normal rotation
      
      setScale(newScale);
    };
    
    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [presentationState?.isActive]);

  // Load competitions list
  useEffect(() => {
    const loadCompetitions = async () => {
      try {
        const response = await axios.get(`${API}/music/competitions`);
        const comps = response.data.competitions || [];
        setCompetitions(comps);
        
        const savedCompId = localStorage.getItem('display_competition_id');
        if (savedCompId) {
          const savedComp = comps.find(c => c.id === savedCompId);
          if (savedComp) {
            setSelectedCompetitionId(savedCompId);
            setCompetition(savedComp);
          } else {
            setShowSetup(true);
          }
        } else {
          setShowSetup(true);
        }
      } catch (error) {
        console.error('Error loading competitions:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCompetitions();
  }, []);

  // Save competition selection and fetch rounds
  useEffect(() => {
    if (selectedCompetitionId) {
      localStorage.setItem('display_competition_id', selectedCompetitionId);
      // Fetch available rounds from programme
      const fetchRounds = async () => {
        try {
          const response = await axios.get(`${API}/music/programme/${selectedCompetitionId}`);
          const programme = response.data?.programme || [];
          // Extract unique round numbers
          const rounds = [...new Set(programme.map(p => p.round).filter(r => r))].sort((a, b) => a - b);
          if (rounds.length > 0) {
            setAvailableRounds(rounds);
          } else {
            setAvailableRounds([1, 2, 3]);
          }
        } catch (error) {
          // Fallback to default rounds if programme fetch fails
          setAvailableRounds([1, 2, 3]);
        }
      };
      fetchRounds();
    }
  }, [selectedCompetitionId]);

  // Fetch display state (timer, now playing, next up)
  const isFetchingRef = useRef(false);          // guard against concurrent polls
  const competitionIdRef = useRef(selectedCompetitionId);
  const selectedRoundRef = useRef(selectedRound);

  // Keep refs in sync so the poll closure never goes stale
  useEffect(() => { competitionIdRef.current = selectedCompetitionId; }, [selectedCompetitionId]);
  useEffect(() => { selectedRoundRef.current = selectedRound; }, [selectedRound]);

  // Single combined poll — timer + nowPlaying + nextUp + presentation in one request batch
  // Using refs avoids re-creating this function on every state change
  const fetchDisplayState = useCallback(async () => {
    const compId = competitionIdRef.current;
    if (!compId) return;

    // Skip if a previous fetch is still in flight (prevents response-ordering flicker)
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const [timerRes, nowPlayingRes, nextUpRes, presentRes] = await Promise.all([
        axios.get(`${API}/timer/state/${compId}`).catch(() => null),
        axios.get(`${API}/now-playing/${compId}`).catch(() => null),
        axios.get(`${API}/music-queue/${compId}/next-up`).catch(() => null),
        axios.get(`${API}/presentation/state/${compId}`).catch(() => null),
      ]);

      // --- Timer ---
      const timerData = timerRes?.data?.timer;
      if (timerData?.isRunning) {
        setTimer(timerData);
        if (!timerData.isPaused) {
          const elapsed = Math.floor((Date.now() - new Date(timerData.startedAt).getTime()) / 1000);
          setDisplayTime(Math.max(0, timerData.duration - elapsed));
        } else {
          setDisplayTime(timerData.remainingTime || 0);
        }
      } else {
        setTimer(null);
        setDisplayTime(0);
      }

      // --- Now Playing / Next Up ---
      setNowPlaying(nowPlayingRes?.data?.nowPlaying || null);
      setNextUp(nextUpRes?.data?.nextUp || null);

      // --- Presentation — only update when display-relevant fields change ---
      const newPres = presentRes?.data || null;
      setPresentationState(prev => {
        const key = (s) => s
          ? `${s.isActive}|${s.category}|${s.apparatus}|${JSON.stringify(s.revealedPlaces)}`
          : 'null';
        if (key(prev) === key(newPres)) return prev;
        return newPres;
      });

    } catch (error) {
      console.error('Error fetching display state:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, []); // stable — reads competition ID from ref, never re-created

  // Fetch latest scores for each apparatus
  const fetchLatestScores = useCallback(async () => {
    const compId = competitionIdRef.current;
    const round = selectedRoundRef.current;
    if (!compId) return;
    
    try {
      const roundParam = round ? `?round=${round}` : '';
      const response = await axios.get(`${API}/display/latest-scores/${compId}${roundParam}`);
      setLatestScores(response.data.scores || {});
    } catch (error) {
      // Fallback: fetch from livescores and organize by apparatus
      try {
        const liveRes = await axios.get(`${API}/livescores/${compId}`);
        let scores = liveRes.data?.scores || [];
        
        if (round) {
          scores = scores.filter(s => s.round === round || s.round === parseInt(round));
        }
        
        const organized = {};
        const apparatusList = displayApparatus.length ? displayApparatus : ['vault', 'bars', 'beam', 'floor', 'range'];
        
        for (const app of apparatusList) {
          organized[app] = { A: null, B: null };
          
          const appScores = scores.filter(s => s.apparatus === app);
          
          const panelAScores = appScores.filter(s => s.panel === 'A' || s.panel === 'a');
          const panelBScores = appScores.filter(s => s.panel === 'B' || s.panel === 'b');
          const singleScores = appScores.filter(s => !s.panel || s.panel === 'single' || s.panel === 'SINGLE PANEL' || s.panel === 'Single');
          
          if (panelAScores.length > 0) {
            organized[app].A = panelAScores.sort((a, b) => 
              new Date(b.submittedAt || b.createdAt) - new Date(a.submittedAt || a.createdAt)
            )[0];
          }
          
          if (panelBScores.length > 0) {
            organized[app].B = panelBScores.sort((a, b) => 
              new Date(b.submittedAt || b.createdAt) - new Date(a.submittedAt || a.createdAt)
            )[0];
          }
          
          if (singleScores.length > 0 && !organized[app].A && !organized[app].B) {
            const latest = singleScores.sort((a, b) => 
              new Date(b.submittedAt || b.createdAt) - new Date(a.submittedAt || a.createdAt)
            )[0];
            organized[app].A = latest;
          }
        }
        
        setLatestScores(organized);
      } catch (err) {
        console.error('Error fetching scores:', err);
      }
    }
  }, []); // stable — reads compId/round from refs

  // Single poll interval — 1000ms is plenty fast for a display screen
  // NOT including fetchDisplayState/fetchLatestScores in deps — both are stable (no-dep useCallback)
  useEffect(() => {
    if (!selectedCompetitionId) return;
    
    fetchDisplayState();
    fetchLatestScores();
    
    const stateInterval = setInterval(fetchDisplayState, 1000);
    const scoresInterval = setInterval(fetchLatestScores, 5000);
    
    return () => {
      clearInterval(stateInterval);
      clearInterval(scoresInterval);
    };
  }, [selectedCompetitionId]); // stable poll functions read compId from ref

  // Timer countdown
  useEffect(() => {
    if (!timer || !timer.isRunning || timer.isPaused) return;
    
    const interval = setInterval(() => {
      setDisplayTime(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [timer]);

  // Poll display control mode + sponsors (every 2 s)
  useEffect(() => {
    if (!selectedCompetitionId) return;
    const fetchControl = async () => {
      try {
        const res = await axios.get(`${API}/display-control/${selectedCompetitionId}`);
        setDisplayControlMode(res.data?.mode || 'normal');
        setSponsors(res.data?.sponsors || []);
      } catch (_) {}
    };
    fetchControl();
    const id = setInterval(fetchControl, 2000);
    return () => clearInterval(id);
  }, [selectedCompetitionId]);

  // 1-second clock tick — used to recompute sponsor slot in sync across all displays
  useEffect(() => {
    const id = setInterval(() => setClockTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Track previous nowPlaying and nextUp to detect changes
  const prevNowPlayingRef = useRef(null);
  const prevNextUpRef = useRef(null);
  const forceInfoUntilRef = useRef(null);

  // When nowPlaying or nextUp changes, force info view for 20 seconds
  useEffect(() => {
    const nowPlayingId = nowPlaying?.id || nowPlaying?.gymnastId;
    const prevNowPlayingId = prevNowPlayingRef.current;
    
    const nextUpId = nextUp?.number ? `${nextUp.number}${nextUp.letter}` : null;
    const prevNextUpId = prevNextUpRef.current;
    
    // Detect if nowPlaying just started (new gymnast)
    if (nowPlayingId && nowPlayingId !== prevNowPlayingId) {
      setDisplayMode('info');
      forceInfoUntilRef.current = Date.now() + 20000; // Force info for 20 seconds
    }
    // Detect if nextUp changed (and no one is currently performing)
    else if (!nowPlaying && nextUpId && nextUpId !== prevNextUpId) {
      setDisplayMode('info');
      forceInfoUntilRef.current = Date.now() + 20000; // Force info for 20 seconds
    }
    
    prevNowPlayingRef.current = nowPlayingId;
    prevNextUpRef.current = nextUpId;
  }, [nowPlaying, nextUp]);

  // Rotation logic: alternate between info and scores every 20 seconds
  // ONLY when NOT showing timer
  useEffect(() => {
    const showTimer = timer && timer.isRunning;
    
    // Don't rotate if timer is showing - timer takes full screen
    if (showTimer) {
      return;
    }
    
    // Rotate between info and scores every 20 seconds
    const rotationInterval = setInterval(() => {
      // Don't rotate away from info if we're in forced info period
      if (forceInfoUntilRef.current && Date.now() < forceInfoUntilRef.current) {
        return;
      }
      setDisplayMode(prev => prev === 'info' ? 'scores' : 'info');
    }, 20000);
    
    return () => clearInterval(rotationInterval);
  }, [timer]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Poll for presentation state - only update if data changed
  useEffect(() => {
    if (!selectedCompetitionId) return;

    const pollPresentation = async () => {
      try {
        const response = await axios.get(`${API}/presentation/state/${selectedCompetitionId}`);
        const newState = response.data;
        
        // Only update if data actually changed
        setPresentationState(prev => {
          if (!prev && !newState?.isActive) return prev;
          if (JSON.stringify(prev) === JSON.stringify(newState)) return prev;
          return newState;
        });
      } catch (error) {
        // Ignore errors - presentation might not be active
      }
    };

    pollPresentation();
    const interval = setInterval(pollPresentation, 500);
    return () => clearInterval(interval);
  }, [selectedCompetitionId]);

  // Presentation 5-second timer effect (runs at parent level)
  useEffect(() => {
    const revealedPlaces = presentationState?.revealedPlaces || [];
    const has1stPlace = revealedPlaces.includes(1);
    
    if (has1stPlace && !showAllResults && !timerStartedRef.current) {
      // 1st place is revealed, start timer
      timerStartedRef.current = true;
      setFirstRevealedAt(Date.now());
      
      const timer = setTimeout(() => {
        setShowAllResults(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    } else if (!has1stPlace && (timerStartedRef.current || showAllResults)) {
      // Reset when 1st place is not revealed
      timerStartedRef.current = false;
      setFirstRevealedAt(null);
      setShowAllResults(false);
    }
  }, [presentationState?.revealedPlaces, showAllResults]);

  // Reset timer when category/apparatus changes
  useEffect(() => {
    timerStartedRef.current = false;
    setFirstRevealedAt(null);
    setShowAllResults(false);
  }, [presentationState?.category, presentationState?.apparatus]);

  // Handle competition selection
  const handleSelectCompetition = () => {
    const comp = competitions.find(c => c.id === selectedCompetitionId);
    if (comp) {
      setCompetition(comp);
      setShowSetup(false);
    }
  };

  // Apparatus display names
  const apparatusNames = {
    vault: 'VAULT',
    bars: 'BARS',
    beam: 'BEAM',
    floor: 'FLOOR',
    range: 'R&C'
  };

  // Derive active apparatus from competition config (canonical key: 'range')
  const displayApparatus = React.useMemo(() => {
    const raw = competition?.competitionApparatus;
    if (!raw?.length) return ['vault', 'bars', 'beam', 'floor', 'range'];
    // Normalise legacy 'randc' → 'range'
    return raw.map(a => (a === 'randc' ? 'range' : a));
  }, [competition]);

  // Display logic flags
  const showGymnast = nowPlaying !== null;  // Show if nowPlaying exists (gymnast is performing)
  const showTimer = timer && timer.isRunning;
  const showCompetition = !showGymnast && !showTimer;

  // ── Sponsor / Timer+Sponsors screen computation ──────────────────────────────
  const effectiveSponsors = sponsors.filter(s => s.logoUrl || s.text);
  const hasSponsors = effectiveSponsors.length > 0;

  // ── Clock-synced sponsor slot ──────────────────────────────────────────────
  // All displays compute the same slot from the wall clock, so /display/a,
  // /display/b and /display are always in sync regardless of when they loaded.
  // Slots tick every 10 seconds, aligned to Unix epoch (e.g. :00, :10, :20 …).
  const sponsorSlot = Math.floor(Date.now() / 10000);

  // Which screen to show (presentation > sms_register > timer_sponsors logic > sponsors > timer > normal)
  let currentScreen; // 'presentation' | 'sms_register' | 'timer' | 'sponsor' | 'normal'
  if (presentationState?.isActive) {
    currentScreen = 'presentation';
  } else if (displayControlMode === 'sms_register') {
    currentScreen = 'sms_register';
  } else if (displayControlMode === 'timer_sponsors' && showTimer) {
    // Alternate: even slot = timer, odd slot = sponsor
    currentScreen = (sponsorSlot % 2 === 1 && hasSponsors) ? 'sponsor' : 'timer';
  } else if ((displayControlMode === 'sponsors' || displayControlMode === 'timer_sponsors') && hasSponsors) {
    // Sponsors mode, or timer_sponsors after timer ended
    currentScreen = 'sponsor';
  } else if (showTimer) {
    currentScreen = 'timer';
  } else {
    currentScreen = 'normal';
  }

  // Which sponsor to show
  const currentSponsorIdx = displayControlMode === 'sponsors'
    ? (effectiveSponsors.length ? sponsorSlot % effectiveSponsors.length : 0)
    : (effectiveSponsors.length ? Math.floor(sponsorSlot / 2) % effectiveSponsors.length : 0);
  const currentSponsor = effectiveSponsors[currentSponsorIdx] || null;

  // ── Sponsor full-screen component ────────────────────────────────────────────
  const SponsorScreen = ({ sponsor }) => {
    const hasLogo = !!sponsor?.logoUrl;
    const hasText = !!sponsor?.text;
    return (
      <div className="w-full h-full bg-white relative overflow-hidden">
        {/* Logo fills the full display */}
        {hasLogo && (
          <img
            src={`${BACKEND_URL}${sponsor.logoUrl}`}
            alt="Sponsor"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: hasText ? '80%' : '100%',
              objectFit: 'contain',
            }}
          />
        )}
        {/* Text anchored to bottom when logo present, else centred */}
        {hasText && (
          <div
            className="absolute left-0 right-0 flex items-center justify-center font-bold text-gray-900 text-center leading-tight"
            style={{
              bottom: 0,
              top: hasLogo ? '80%' : 0,
              padding: '1vh 4vw',
              fontSize: hasLogo ? 'clamp(36px, 5vw, 96px)' : 'clamp(48px, 8vw, 140px)',
            }}
          >
            {sponsor.text}
          </div>
        )}
        {!hasLogo && !hasText && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-gray-300 text-2xl">No sponsor content</p>
          </div>
        )}
      </div>
    );
  };

  // ── SMS Register full-screen component ───────────────────────────────────────
  const SmsRegisterScreen = () => (
    <div className="w-full h-full bg-white flex flex-col items-center justify-center gap-8 px-16 py-12">
      <img src="/scorz-logo.png" alt="Scorz" className="h-28 mx-auto" onError={(e) => e.target.style.display = 'none'} />
      <p
        className="text-gray-800 text-center font-semibold leading-snug max-w-4xl"
        style={{ fontSize: 'clamp(28px, 3.5vw, 64px)' }}
      >
        Scan the on-screen QR Code or visit the link below to register for instant SMS messages when your gymnast receives a score.
      </p>
      <QRCodeSVG value="https://scorz.co.uk/register" size={280} level="M" />
      <p
        className="text-gray-600 font-bold tracking-wide"
        style={{ fontSize: 'clamp(24px, 3vw, 56px)' }}
      >
        scorz.co.uk/register
      </p>
    </div>
  );

  // Setup screen
  if (showSetup || loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-8">
        <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl">
          <h1 className="text-2xl font-bold text-center mb-6">Display Setup</h1>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Competition
              </label>
              <Select value={selectedCompetitionId || ''} onValueChange={setSelectedCompetitionId}>
                <SelectTrigger data-testid="competition-select">
                  <SelectValue placeholder="Choose a competition" />
                </SelectTrigger>
                <SelectContent>
                  {competitions.map(comp => (
                    <SelectItem key={comp.id} value={comp.id}>
                      {comp.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Round (Optional)
              </label>
              <Select value={selectedRound || 'all'} onValueChange={(v) => setSelectedRound(v === 'all' ? null : v)}>
                <SelectTrigger data-testid="round-select">
                  <SelectValue placeholder="All rounds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Rounds</SelectItem>
                  {availableRounds.map(round => (
                    <SelectItem key={round} value={String(round)}>Round {round}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              onClick={handleSelectCompetition}
              disabled={!selectedCompetitionId}
              className="w-full bg-black hover:bg-gray-800"
              data-testid="start-display-btn"
            >
              Start Display
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Scores Display Component
  // Get the most recent score for an apparatus (either panel)
  const getLatestScore = (apparatus) => {
    const scores = latestScores[apparatus] || { A: null, B: null };
    // Return the most recent score from either panel
    if (scores.A && scores.B) {
      const aTime = new Date(scores.A.submittedAt || scores.A.createdAt || 0);
      const bTime = new Date(scores.B.submittedAt || scores.B.createdAt || 0);
      return aTime > bTime ? scores.A : scores.B;
    }
    return scores.A || scores.B || null;
  };

  // Get scores for both panels
  const getPanelScores = (apparatus) => {
    return latestScores[apparatus] || { A: null, B: null };
  };

  // Alternating colors for visual interest
  const panelColors = {
    vault: 'bg-white',
    bars: 'bg-gray-100',
    beam: 'bg-white',
    floor: 'bg-gray-100',
    range: 'bg-white'
  };

  const ScoresDisplay = () => (
    <div className="w-full h-full bg-white flex flex-col">
      {/* Header with Scorz Logo - fixed position */}
      <div className="h-16 flex items-center justify-center bg-white border-b border-gray-200 flex-shrink-0">
        <img 
          src="/scorz-logo.png" 
          alt="Scorz" 
          className="h-12"
        />
      </div>
      
      {/* Score Grid - equal columns, fills full width */}
      <div className="flex-1 flex">
        {displayApparatus.map((apparatus) => {
          const scores = getPanelScores(apparatus);
          
          return (
            <div 
              key={apparatus} 
              className={`${panelColors[apparatus] || 'bg-white'} flex flex-col border-r border-gray-300 last:border-r-0`}
              style={{ flex: 1 }}
            >
              {/* Apparatus Name Header */}
              <div className="h-14 flex items-center justify-center bg-white border-b border-gray-200 flex-shrink-0">
                <span className="text-black text-2xl font-bold tracking-[0.2em] uppercase">
                  {apparatusNames[apparatus]}
                </span>
              </div>
              
              {/* Panel A - Top Half */}
              <div className="flex-1 flex flex-col items-center justify-between py-4 border-b-2 border-gray-400">
                {scores.A ? (
                  <>
                    {/* Name at top - stacked and auto-sized */}
                    <div className="flex-1 flex flex-col items-center justify-center w-full px-2">
                      {(() => {
                        const name = scores.A.gymnastName || `#${scores.A.gymnastNumber || '-'}`;
                        const parts = name.split(' ');
                        const firstName = parts[0] || '';
                        const lastName = parts.slice(1).join(' ') || '';
                        const longestPart = firstName.length > lastName.length ? firstName : lastName;
                        // Auto-size: bigger for short names, smaller for long names
                        const fontSize = longestPart.length <= 6 ? '100px' : 
                                        longestPart.length <= 8 ? '85px' : 
                                        longestPart.length <= 10 ? '70px' : 
                                        longestPart.length <= 12 ? '60px' : '50px';
                        return (
                          <>
                            <span className="font-black text-gray-900 text-center leading-none" style={{ fontSize }}>
                              {firstName}
                            </span>
                            {lastName && (
                              <span className="font-black text-gray-900 text-center leading-none mt-1" style={{ fontSize }}>
                                {lastName}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {/* Score box at bottom */}
                    <div className="flex flex-col items-center gap-1">
                      {scores.A.isCorrected && (
                        <span className="text-xs font-black tracking-widest text-orange-600 uppercase bg-orange-100 px-3 py-0.5 rounded">CORRECTION</span>
                      )}
                      <div className="bg-black px-6 py-3 rounded-lg">
                        <span 
                          className="font-black text-white tabular-nums leading-none"
                          style={{ fontSize: '60px' }}
                        >
                          {parseFloat(scores.A.finalScore || 0).toFixed(3)}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex-1 flex items-center justify-center">
                      <span className="text-4xl text-gray-300">—</span>
                    </div>
                    <span className="text-sm text-gray-400 mb-2">PANEL A</span>
                  </>
                )}
              </div>
              
              {/* Panel B - Bottom Half */}
              <div className="flex-1 flex flex-col items-center justify-between py-4">
                {scores.B ? (
                  <>
                    {/* Name at top - stacked and auto-sized */}
                    <div className="flex-1 flex flex-col items-center justify-center w-full px-2">
                      {(() => {
                        const name = scores.B.gymnastName || `#${scores.B.gymnastNumber || '-'}`;
                        const parts = name.split(' ');
                        const firstName = parts[0] || '';
                        const lastName = parts.slice(1).join(' ') || '';
                        const longestPart = firstName.length > lastName.length ? firstName : lastName;
                        // Auto-size: bigger for short names, smaller for long names
                        const fontSize = longestPart.length <= 6 ? '100px' : 
                                        longestPart.length <= 8 ? '85px' : 
                                        longestPart.length <= 10 ? '70px' : 
                                        longestPart.length <= 12 ? '60px' : '50px';
                        return (
                          <>
                            <span className="font-black text-gray-900 text-center leading-none" style={{ fontSize }}>
                              {firstName}
                            </span>
                            {lastName && (
                              <span className="font-black text-gray-900 text-center leading-none mt-1" style={{ fontSize }}>
                                {lastName}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {/* Score box at bottom */}
                    <div className="flex flex-col items-center gap-1">
                      {scores.B.isCorrected && (
                        <span className="text-xs font-black tracking-widest text-orange-600 uppercase bg-orange-100 px-3 py-0.5 rounded">CORRECTION</span>
                      )}
                      <div className="bg-black px-6 py-3 rounded-lg">
                        <span 
                          className="font-black text-white tabular-nums leading-none"
                          style={{ fontSize: '60px' }}
                        >
                          {parseFloat(scores.B.finalScore || 0).toFixed(3)}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex-1 flex items-center justify-center">
                      <span className="text-4xl text-gray-300">—</span>
                    </div>
                    <span className="text-sm text-gray-400 mb-2">PANEL B</span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Presentation Display Component - White Background with Black Text/Logo
  const PresentationDisplay = () => {
    if (!presentationState?.isActive) return null;

    const rankings = presentationState.rankings || [];
    const revealedPlaces = presentationState.revealedPlaces || [];
    const apparatus = presentationState.apparatus;
    const category = presentationState.category;

    // Get apparatus display name
    const apparatusDisplayNames = {
      vault: 'VAULT',
      bars: 'BARS',
      beam: 'BEAM',
      floor: 'FLOOR',
      range: 'RANGE & CONDITIONING',
      randc: 'RANGE & CONDITIONING',
      aa: 'ALL AROUND'
    };

    // Group by rank for joint handling
    const groupedByRank = {};
    rankings.forEach(r => {
      const rank = r.rank;
      if (rank != null) {
        if (!groupedByRank[rank]) groupedByRank[rank] = [];
        groupedByRank[rank].push(r);
      }
    });

    // Determine which place to show - ONLY the most recently revealed (lowest number)
    const sortedRevealed = [...revealedPlaces].sort((a, b) => a - b);
    const currentPlace = sortedRevealed.length > 0 ? sortedRevealed[0] : null;
    const showingFirst = currentPlace === 1;
    const showingSecond = currentPlace === 2;
    const showingThird = currentPlace === 3;
    
    // showAllResults is now managed at parent level and passed through closure

    const getRankSuffix = (rank) => {
      if (rank === 1) return 'ST';
      if (rank === 2) return 'ND';
      if (rank === 3) return 'RD';
      return 'TH';
    };

    // Background colors for medal positions
    const getBackgroundStyle = () => {
      if (showAllResults) return 'bg-white';
      if (showingFirst) return 'bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500';
      if (showingSecond) return 'bg-gradient-to-b from-gray-200 via-gray-300 to-gray-400';
      if (showingThird) return 'bg-gradient-to-b from-amber-500 via-amber-600 to-amber-700';
      return 'bg-white';
    };

    const getTextColor = () => {
      if (showAllResults) return 'text-black';
      if (showingFirst || showingSecond) return 'text-black';
      if (showingThird) return 'text-white';
      return 'text-black';
    };

    // Get gymnasts to display
    const getDisplayGymnasts = () => {
      if (showAllResults) {
        // Show all revealed gymnasts
        const allRevealed = [];
        sortedRevealed.forEach(rank => {
          const gymnasts = groupedByRank[rank] || [];
          gymnasts.forEach(g => allRevealed.push({ ...g, rank }));
        });
        return allRevealed;
      } else if (currentPlace != null) {
        // Show only current place
        const gymnasts = groupedByRank[currentPlace] || [];
        return gymnasts.map(g => ({ ...g, rank: currentPlace }));
      }
      return [];
    };

    const displayGymnasts = getDisplayGymnasts();
    const jointCount = displayGymnasts.length;

    return (
      <div className={`w-full h-full flex flex-col overflow-hidden transition-colors duration-500 ${getBackgroundStyle()}`}>
        {/* Content - FULL SCREEN, no header/footer */}
        <div className="flex-1 flex flex-col items-center justify-center overflow-hidden">
          {revealedPlaces.length === 0 ? (
            <div className="text-center">
              <p className="text-4xl text-gray-300 tracking-widest">AWAITING RESULTS</p>
            </div>
          ) : showAllResults ? (
            /* Full Results View - Maximize row sizes */
            <div className="w-full h-full flex flex-col justify-center px-8 py-4">
              <div className="text-center mb-4">
                <p className="text-2xl text-gray-400 uppercase tracking-[0.3em]">FINAL RESULTS — {(apparatusDisplayNames[apparatus] || apparatus).toUpperCase()}</p>
              </div>
              <div className="flex-1 flex flex-col justify-center space-y-3 max-h-[900px]">
                {displayGymnasts.map((gymnast, idx) => {
                  const isFirst = gymnast.rank === 1;
                  const isSecond = gymnast.rank === 2;
                  const isThird = gymnast.rank === 3;
                  const isJoint = groupedByRank[gymnast.rank]?.length > 1;
                  
                  let bgClass = 'bg-gray-100 text-black';
                  if (isFirst) bgClass = 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black';
                  if (isSecond) bgClass = 'bg-gradient-to-r from-gray-300 to-gray-400 text-black';
                  if (isThird) bgClass = 'bg-gradient-to-r from-amber-500 to-amber-600 text-white';
                  
                  // Dynamic sizing based on number of results
                  const rowHeight = Math.min(140, Math.floor(850 / displayGymnasts.length));
                  const fontSize = Math.min(48, Math.floor(rowHeight * 0.4));
                  const rankSize = Math.min(60, Math.floor(rowHeight * 0.5));
                  
                  return (
                    <div 
                      key={`${gymnast.gymnastId}-${gymnast.rank}`} 
                      className={`rounded-2xl flex items-center px-8 ${bgClass}`}
                      style={{ height: `${rowHeight}px` }}
                    >
                      <div 
                        className={`rounded-full flex items-center justify-center font-black flex-shrink-0 ${isFirst ? 'bg-yellow-600 text-white' : isSecond ? 'bg-gray-600 text-white' : isThird ? 'bg-amber-800 text-white' : 'bg-black text-white'}`}
                        style={{ width: `${rankSize + 20}px`, height: `${rankSize + 20}px` }}
                      >
                        <span style={{ fontSize: `${rankSize * 0.6}px` }}>{gymnast.rank}</span>
                        <span style={{ fontSize: `${rankSize * 0.25}px` }}>{getRankSuffix(gymnast.rank)}</span>
                      </div>
                      <div className="flex-1 min-w-0 ml-6">
                        <div className="font-black truncate leading-tight" style={{ fontSize: `${fontSize}px` }}>
                          {isJoint && '= '}{gymnast.name?.toUpperCase() || 'UNKNOWN'}
                        </div>
                        <div className="opacity-60 truncate" style={{ fontSize: `${fontSize * 0.45}px` }}>{gymnast.club || ''}</div>
                      </div>
                      <div className={`text-right px-6 py-2 rounded-xl font-mono font-black ${isFirst ? 'bg-yellow-600/40' : isSecond ? 'bg-gray-600/40' : isThird ? 'bg-amber-800/40' : 'bg-black/20'}`}>
                        <span style={{ fontSize: `${fontSize}px` }}>
                          {apparatus === 'aa' 
                            ? (typeof gymnast.aaScore === 'number' ? gymnast.aaScore.toFixed(3) : gymnast.aaScore || '-')
                            : (gymnast.score?.finalScore != null 
                                ? (typeof gymnast.score.finalScore === 'number' 
                                    ? gymnast.score.finalScore.toFixed(3) 
                                    : parseFloat(gymnast.score.finalScore).toFixed(3))
                                : '-')
                          }
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Single Place View - MASSIVE TEXT */
            <div className="w-full h-full flex flex-col items-center justify-center px-4">
              {jointCount === 1 ? (
                /* Single gymnast - MAXIMUM SIZE */
                <>
                  <div className={`font-black leading-none ${getTextColor()}`} style={{ fontSize: '280px' }}>
                    {currentPlace}<sup style={{ fontSize: '100px' }}>{getRankSuffix(currentPlace)}</sup>
                  </div>
                  <div className={`font-black text-center leading-tight mt-4 ${getTextColor()}`} style={{ fontSize: '140px' }}>
                    {displayGymnasts[0]?.name?.toUpperCase() || 'UNKNOWN'}
                  </div>
                  <div className={`text-center mt-2 ${showingThird ? 'text-amber-200' : 'text-gray-500'}`} style={{ fontSize: '60px' }}>
                    {displayGymnasts[0]?.club || ''}
                  </div>
                  <div className={`mt-8 px-12 py-6 rounded-2xl ${showingFirst ? 'bg-yellow-600 text-white' : showingSecond ? 'bg-gray-600 text-white' : showingThird ? 'bg-amber-800 text-white' : 'bg-black text-white'}`}>
                    <div className="font-mono font-black" style={{ fontSize: '80px' }}>
                      {apparatus === 'aa' 
                        ? (typeof displayGymnasts[0]?.aaScore === 'number' ? displayGymnasts[0].aaScore.toFixed(3) : displayGymnasts[0]?.aaScore || '-')
                        : (displayGymnasts[0]?.score?.finalScore != null 
                            ? (typeof displayGymnasts[0].score.finalScore === 'number' 
                                ? displayGymnasts[0].score.finalScore.toFixed(3) 
                                : parseFloat(displayGymnasts[0].score.finalScore).toFixed(3))
                            : '-')
                      }
                    </div>
                  </div>
                </>
              ) : (
                /* Joint places - scale down based on count */
                <>
                  <div className={`font-black tracking-widest mb-4 ${getTextColor()}`} style={{ fontSize: '60px' }}>
                    = JOINT {currentPlace}{getRankSuffix(currentPlace)} PLACE =
                  </div>
                  <div className="w-full flex flex-col items-center space-y-4">
                    {displayGymnasts.map((gymnast, idx) => {
                      // Scale based on joint count: 2=large, 3=medium, 4+=small
                      const nameSize = jointCount === 2 ? 100 : jointCount === 3 ? 80 : 60;
                      const clubSize = jointCount === 2 ? 40 : jointCount === 3 ? 32 : 24;
                      const scoreSize = jointCount === 2 ? 60 : jointCount === 3 ? 48 : 36;
                      
                      return (
                        <div key={`${gymnast.gymnastId}-${gymnast.rank}`} className="text-center">
                          <div className={`font-black leading-tight ${getTextColor()}`} style={{ fontSize: `${nameSize}px` }}>
                            {gymnast.name?.toUpperCase() || 'UNKNOWN'}
                          </div>
                          <div className={`${showingThird ? 'text-amber-200' : 'text-gray-500'}`} style={{ fontSize: `${clubSize}px` }}>
                            {gymnast.club || ''}
                          </div>
                          <div className={`inline-block mt-2 px-6 py-2 rounded-xl ${showingFirst ? 'bg-yellow-600 text-white' : showingSecond ? 'bg-gray-600 text-white' : showingThird ? 'bg-amber-800 text-white' : 'bg-black text-white'}`}>
                            <span className="font-mono font-black" style={{ fontSize: `${scoreSize}px` }}>
                              {apparatus === 'aa' 
                                ? (typeof gymnast.aaScore === 'number' ? gymnast.aaScore.toFixed(3) : gymnast.aaScore || '-')
                                : (gymnast.score?.finalScore != null 
                                    ? (typeof gymnast.score.finalScore === 'number' 
                                        ? gymnast.score.finalScore.toFixed(3) 
                                        : parseFloat(gymnast.score.finalScore).toFixed(3))
                                    : '-')
                              }
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-screen h-screen bg-black overflow-hidden flex items-center justify-center">
      {/* Scaled container for 16:9 content - always maintains aspect ratio */}
      <div 
        className="relative bg-white"
        style={{
          width: '1920px',
          height: '1080px',
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          aspectRatio: '16/9'
        }}
      >
        {/* Control buttons — always visible, fade in fullscreen */}
        <div className={`absolute top-8 right-8 flex gap-3 transition-opacity duration-300 z-50 ${isFullscreen ? 'opacity-0 hover:opacity-100' : 'opacity-100'}`}>
          {/* Presentation mode indicator */}
          {presentationState?.isActive ? (
            <button
              onClick={stopPresentation}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold shadow-lg transition-all"
              data-testid="stop-presentation-btn"
              title="Stop Presentations — switch back to normal display"
            >
              <Square className="h-4 w-4 fill-white" />
              STOP PRESENTATION
            </button>
          ) : (
            <a
              href="/presentations"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-white/80 hover:bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:text-black transition-all"
              data-testid="go-to-presentations-link"
              title="Open Presentations control panel"
            >
              <Radio className="h-4 w-4" />
              Presentations
            </a>
          )}
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowSetup(true)}
            className="bg-white/80 hover:bg-white w-12 h-12"
            data-testid="settings-btn"
          >
            <Settings className="h-6 w-6" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={toggleFullscreen}
            className="bg-white/80 hover:bg-white w-12 h-12"
            data-testid="fullscreen-btn"
          >
            {isFullscreen ? <X className="h-6 w-6" /> : <Maximize className="h-6 w-6" />}
          </Button>
        </div>

        {/* Main Content */}
        {currentScreen === 'presentation' ? (
          /* Presentation Display - Takes priority when active, full 16:9 */
          <div className="w-full h-full">
            <PresentationDisplay />
          </div>
        ) : currentScreen === 'sms_register' ? (
          /* SMS Register Screen */
          <SmsRegisterScreen />
        ) : currentScreen === 'sponsor' ? (
          /* Sponsor / Timer+Sponsors screen */
          <SponsorScreen sponsor={currentSponsor} />
        ) : currentScreen === 'timer' ? (
          /* Timer Display - No rotation, just timer */
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div className="text-7xl font-semibold text-gray-700 uppercase tracking-wide mb-4">
              {timer.mode === 'apparatus' ? 'Apparatus Warm-up' : 'General Warm-up'}
            </div>
            <div 
              className={`text-[500px] font-black leading-none tabular-nums ${displayTime <= 10 ? 'text-red-600' : 'text-gray-900'}`}
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              data-testid="timer-display"
            >
              {formatTime(displayTime)}
            </div>
            {timer.isPaused && (
              <div className="text-8xl font-bold text-yellow-600 animate-pulse mt-4" data-testid="paused-indicator">
                PAUSED
              </div>
            )}
            {nextUp && (
              <div className="mt-8 p-6 bg-blue-50 rounded-2xl inline-block">
                <div className="flex items-center justify-center mb-2">
                  <User className="w-10 h-10 mr-3 text-blue-600" />
                  <div className="text-4xl font-semibold text-blue-700">Next Up on Floor</div>
                </div>
                <div className="text-5xl font-bold text-gray-800">
                  {nextUp.number}{nextUp.letter} - {nextUp.firstName} {nextUp.lastName}
                </div>
              </div>
            )}
          </div>
        ) : displayMode === 'scores' ? (
          /* Scores Display */
          <ScoresDisplay />
        ) : (
          /* Info Display - Priority: Now Playing > Next Up > Competition Name */
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            {showGymnast ? (
              /* Floor music playing - show Now Performing */
              <>
                <div className="text-6xl font-semibold text-green-600 uppercase tracking-widest animate-pulse mb-4">
                  Now Performing
                </div>
                <div className="text-[420px] font-black text-gray-900 leading-none tabular-nums" data-testid="gymnast-number">
                  {nowPlaying.gymnastNumber}{nowPlaying.gymnastLetter}
                </div>
                <div className="text-[140px] font-bold text-gray-800 leading-none mt-2" data-testid="gymnast-name">
                  {nowPlaying.gymnastName}
                </div>
                <div className="text-7xl font-semibold text-gray-500 leading-none mt-4" data-testid="club-name">
                  {nowPlaying.clubName}
                </div>
              </>
            ) : nextUp ? (
              /* Next up in queue - show Next Up */
              <>
                <div className="text-6xl font-semibold text-blue-600 uppercase tracking-widest mb-4">
                  Next Up
                </div>
                <div className="text-[420px] font-black text-gray-900 leading-none tabular-nums" data-testid="next-up-number">
                  {nextUp.number}{nextUp.letter}
                </div>
                <div className="text-[140px] font-bold text-gray-800 leading-none mt-2" data-testid="next-up-name">
                  {nextUp.firstName} {nextUp.lastName}
                </div>
                <div className="text-7xl font-semibold text-gray-500 leading-none mt-4" data-testid="next-up-club">
                  {nextUp.clubName}
                </div>
              </>
            ) : competition ? (
              /* Default - show competition name */
              <>
                <div className="mb-8">
                  <img 
                    src="/scorz-logo.png" 
                    alt="Scorz" 
                    className="h-40 mx-auto opacity-80"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
                <div className="text-[120px] font-bold text-gray-800 leading-tight px-4" data-testid="competition-title">
                  {competition.title}
                </div>
                <div className="text-6xl font-semibold text-gray-500 mt-6">
                  {new Date().toLocaleDateString('en-GB', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
                <div className="mt-16 text-5xl text-gray-400">
                  Waiting for performances...
                </div>
              </>
            ) : (
              /* No competition selected */
              <div className="text-center">
                <div className="text-[200px] font-bold text-gray-300 mb-8">
                  --:--
                </div>
                <div className="text-5xl text-gray-400">
                  No competition selected
                </div>
                <Button 
                  onClick={() => setShowSetup(true)} 
                  className="mt-12 text-2xl px-10 py-6 h-auto"
                  data-testid="select-competition-btn"
                >
                  Select Competition
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Competition indicator */}
        <div className={`absolute bottom-8 left-8 text-xl text-gray-400 transition-opacity ${isFullscreen ? 'opacity-0' : 'opacity-100'}`}>
          {competition?.title}
        </div>
        
        {/* Mode indicator */}
        {!showTimer && (
          <div className={`absolute bottom-8 right-8 flex gap-2 transition-opacity ${isFullscreen ? 'opacity-0' : 'opacity-100'}`}>
            <div className={`w-3 h-3 rounded-full ${displayMode === 'info' ? 'bg-green-500' : 'bg-gray-300'}`} />
            <div className={`w-3 h-3 rounded-full ${displayMode === 'scores' ? 'bg-green-500' : 'bg-gray-300'}`} />
          </div>
        )}
      </div>
    </div>
  );
}
